'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface QuoteFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  serviceType: string;
  patioSize: string;
  message: string;
}

const QuoteFormModal = ({ isOpen, onClose }: QuoteFormModalProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    serviceType: '',
    patioSize: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isOpen && isHydrated) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isHydrated]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('/api/send-quote-popup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        setSubmitError(result.error || 'Failed to submit your quote request. Please try again.');
        setIsSubmitting(false);
        return;
      }

      setIsSuccess(true);

      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          serviceType: '',
          patioSize: '',
          message: ''
        });
        onClose();
      }, 3000);
    } catch {
      setSubmitError('A network error occurred. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isHydrated || !isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-background rounded-lg shadow-brand-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border p-6 flex items-center justify-between">
          <h2 className="font-headline font-bold text-2xl text-foreground">Request Free Quote</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-muted transition-brand flex items-center justify-center"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} className="text-foreground" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6">
          {isSuccess ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircleIcon" size={48} className="text-success" variant="solid" />
              </div>
              <h3 className="font-headline font-bold text-2xl text-foreground mb-3">Quote Request Received!</h3>
              <p className="font-body text-muted-foreground text-lg">
                We'll contact you within 24 hours with your personalized quote.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {submitError && (
                <div className="flex items-start space-x-3 p-4 rounded-lg bg-red-50 border border-red-200">
                  <Icon name="ExclamationCircleIcon" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 font-body">{submitError}</p>
                </div>
              )}
              {/* Name & Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block font-body font-medium text-foreground mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block font-body font-medium text-foreground mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              {/* Phone & City */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block font-body font-medium text-foreground mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="(602) 555-0123"
                  />
                </div>
                <div>
                  <label htmlFor="city" className="block font-body font-medium text-foreground mb-2">
                    City *
                  </label>
                  <select
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Select City</option>
                    <option value="Phoenix">Phoenix</option>
                    <option value="Scottsdale">Scottsdale</option>
                    <option value="Tempe">Tempe</option>
                    <option value="Mesa">Mesa</option>
                    <option value="Gilbert">Gilbert</option>
                    <option value="Chandler">Chandler</option>
                    <option value="Glendale">Glendale</option>
                    <option value="Peoria">Peoria</option>
                  </select>
                </div>
              </div>

              {/* Address */}
              <div>
                <label htmlFor="address" className="block font-body font-medium text-foreground mb-2">
                  Property Address *
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="123 Main Street"
                />
              </div>

              {/* Service Type & Patio Size */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="serviceType" className="block font-body font-medium text-foreground mb-2">
                    Service Type *
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Select Service</option>
                    <option value="Patio Cleaning">Patio Cleaning</option>
                    <option value="Paver Restoration">Paver Restoration</option>
                    <option value="Pool Deck Cleaning">Pool Deck Cleaning</option>
                    <option value="Commercial Services">Commercial Services</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="patioSize" className="block font-body font-medium text-foreground mb-2">
                    Approximate Size *
                  </label>
                  <select
                    id="patioSize"
                    name="patioSize"
                    value={formData.patioSize}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Select Size</option>
                    <option value="Small (< 200 sq ft)">Small (&lt; 200 sq ft)</option>
                    <option value="Medium (200-500 sq ft)">Medium (200-500 sq ft)</option>
                    <option value="Large (500-1000 sq ft)">Large (500-1000 sq ft)</option>
                    <option value="Extra Large (> 1000 sq ft)">Extra Large (&gt; 1000 sq ft)</option>
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block font-body font-medium text-foreground mb-2">
                  Additional Details (Optional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  placeholder="Tell us about any specific concerns or requirements..."
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-accent text-accent-foreground rounded-lg font-body font-semibold text-lg hover:bg-accent/90 transition-brand shadow-cta-glow hover:shadow-cta-glow-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-accent-foreground border-t-transparent rounded-full animate-spin mr-3"></div>
                    Submitting...
                  </>
                ) : (
                  'Get Free Quote'
                )}
              </button>

              <p className="text-sm font-body text-muted-foreground text-center">
                We'll respond within 24 hours with your personalized quote
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteFormModal;